import L, { latLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useState } from 'react';
import {
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet';
import { alertColorScale } from 'utils/alertUtils';
import { isMarkerInJurisdiction, isPointInPolygon } from 'utils/geometryUtils';
import './MoveablePoints.css'; // Import custom CSS for styling

// Fixes missing marker icons in Leaflet from rspack update
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import LocationSetter from './LocationSetter';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});
//This is necessary to ensure that the marker icons are displayed correctly in the Leaflet map.
/////////////////////////////////////////////////////////////

// Create custom "X" icon for alertData.coordinates
const xIcon = new L.DivIcon({
  className: 'custom-x-icon',
  html: '<div style="color: red; font-size: 24px;">X</div>',
  iconSize: [20, 20],
  iconAnchor: [10, 18],
});

// Create custom error icon for out-of-jurisdiction markers
const errorIcon = new L.DivIcon({
  className: 'custom-error-icon',
  html: '<div style="background-color: red; color: white; border-radius: 50%; width: 25px; height: 25px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.3);">!</div>',
  iconSize: [25, 25],
  iconAnchor: [12, 25],
});

function DraggableMarker({ position, onMarkerDragEnd, markerIndex, isOutOfJurisdiction }) {
  const eventHandlers = {
    mousedown: (e) => {
      if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
    },
    click: (e) => {
      if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
    },
    dragend: (e) => {
      if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
      const newPosition = e.target.getLatLng();
      onMarkerDragEnd(markerIndex, newPosition);
    },
  };

  return (
    <Marker
      position={position}
      draggable={true}
      eventHandlers={eventHandlers}
      icon={isOutOfJurisdiction ? errorIcon : new L.Icon.Default()}
    >
      {isOutOfJurisdiction && (
        <Popup>
          <div>
            <strong>⚠️ Out of Jurisdiction</strong>
            <br />
            This marker is outside the jurisdiction boundaries.
            <br />
            Coordinates: [{position.lat.toFixed(6)}, {position.lng.toFixed(6)}]
          </div>
        </Popup>
      )}
    </Marker>
  );
}

// MapEvents Component to handle map interactions
function MapEvents({ addMarker }) {
  useMapEvents({
    click: (e) => {
      addMarker(e.latlng); // Pass the latlng of the click to the addMarker function
    },
  });
  return null;
}

function MoveablePoints({
  mapCenter,
  zoom,
  alertData,
  setAlertData,
  isMobile,
  bounds,
  setGetMapCenter,
  jurisdictions,
  widget,
}) {
  // Function to calculate distance between two points
  const calculateDistance = (latlng1, latlng2) => {
    const latDiff = latlng2.lat - latlng1.lat;
    const lngDiff = latlng2.lng - latlng1.lng;
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
  };

  // Function to add a marker based on proximity to the head or tail of the list
  const addMarker = useCallback(
    (latlng) => {
      // Check if the marker is within jurisdiction boundaries
      if (!isMarkerInJurisdiction(latlng, jurisdictions)) {
        console.warn(
          `⚠️ Warning: Marker placed outside jurisdiction boundaries at coordinates [${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}]`,
        );
      }

      if (alertData.coordinates.length === 0) {
        // If there are no alertData.coordinates, just add it as the first marker
        setAlertData((p) => {
          return {
            ...p,
            coordinates: [latlng],
            lastInserted: [latlng],
          };
        });
      } else {
        const head = alertData.coordinates[0];
        const tail = alertData.coordinates[alertData.coordinates.length - 1];

        const distanceToHead = calculateDistance(latlng, head);
        const distanceToTail = calculateDistance(latlng, tail);

        if (distanceToHead < distanceToTail) {
          // Add new marker at the head (start of the list)
          setAlertData((p) => {
            return {
              ...p,
              coordinates: [latlng, ...p.coordinates],
              lastInserted: [...p.lastInserted, latlng],
            };
          });
        } else {
          // Add new marker at the tail (end of the list)
          setAlertData((p) => {
            return {
              ...p,
              coordinates: [...p.coordinates, latlng],
              lastInserted: [...p.lastInserted, latlng],
            };
          });
        }
      }
    },
    [alertData.coordinates, jurisdictions],
  );

  // Function to update the marker's position after dragging
  const handleMarkerDragEnd = useCallback((index, newPosition) => {
    // Check if the new position is within jurisdiction boundaries
    if (!isMarkerInJurisdiction(newPosition, jurisdictions)) {
      console.warn(
        `⚠️ Warning: Marker dragged outside jurisdiction boundaries at coordinates [${newPosition.lat.toFixed(6)}, ${newPosition.lng.toFixed(6)}]`,
      );
    }

    setAlertData((p) => {
      return {
        ...p,
        coordinates: p.coordinates.map((marker, i) => (i === index ? newPosition : marker)),
        lastInserted: p.lastInserted.map((inserted) =>
          inserted === p.coordinates[index] ? newPosition : inserted,
        ),
      };
    });
  }, []);

  return (
    <MapContainer className="w-100 h-100" center={mapCenter} zoom={zoom}>
      <LocationSetter center={mapCenter} zoom={zoom} />
      <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" noWrap={true} />

      {/* Handle map click events */}
      <MapEvents addMarker={addMarker} />

      <Polygon
        pathOptions={{ color: alertColorScale(alertData.severity) }}
        positions={alertData.coordinates}
      />

      {/* Render a draggable marker for each point */}
      {alertData?.coordinates &&
        alertData.coordinates.map((position, index) => {
          const isOutOfJurisdiction = !isMarkerInJurisdiction(position, jurisdictions);
          return (
            <DraggableMarker
              key={index}
              position={position}
              markerIndex={index}
              onMarkerDragEnd={handleMarkerDragEnd}
              isOutOfJurisdiction={isOutOfJurisdiction}
            />
          );
        })}

      {jurisdictions &&
        jurisdictions.map((jurisdiction, index) => {
          return (
            <Polygon
              key={index}
              positions={JSON.parse(jurisdiction.original_coordinates)[0].map(
                ([longitude, latitude]) => [latitude, longitude],
              )}
              color="purple"
              fillOpacity={0}
            />
          );
        })}
    </MapContainer>
  );
}

export default MoveablePoints;
